import moment from "moment";
import { KEYWORD_MATCH_TYPE_OPTIONS, OPERATOR_OPTIONS, TARGET_MATCH_TYPE_OPTIONS } from "./constants";

const scopeText = (scope) => {
  const fmtDate = (date) => {
    if (!date) return null;
    return moment(Date.parse(date)).format("MMM D 'YY");
  };

  if (!scope) return null;
  if (scope.operator === "between") {
    return `between ${
      fmtDate(scope.values.start_date) || "start date"
    } and ${fmtDate(scope.values.end_date || "end date")}`;
  }
  if (scope.operator === "before") {
    return `within ${scope.values.days} days since the last action`;
  }
  if (scope.operator === "after") {
    return `after ${scope.values.days} or more days since the last action`;
  }
};

const makeConditionOptions = (scope, engine = "pricing") => {
  const makeTargetConditionOption = (accessor, label, format = "multi-select") => {
    let matchTypeOptions = [];

    if (accessor === "keyword_match_types") {
      matchTypeOptions = KEYWORD_MATCH_TYPE_OPTIONS;
    }

    if (accessor === "target_match_types") {
      matchTypeOptions = TARGET_MATCH_TYPE_OPTIONS;
    }

    return {
      group: accessor,
      value: `\${${accessor}}`,
      label: label[0].toUpperCase() + label.slice(1),
      activeLabel: label[0].toUpperCase() + label.slice(1),
      format: "multi-select",
      selectOptions: matchTypeOptions,
      supportedOperators: OPERATOR_OPTIONS[format],
    };
  };

  const makeSearchQueryConditionOption = (accessor, label) => {
    return {
      group: accessor,
      supportedOperators: OPERATOR_OPTIONS["text"],
      value: `\${${accessor}}`,
      label: label[0].toUpperCase() + label.slice(1),
      showScope: false,
    };
  };

  const makeBiddingConditionOption = (
    group,
    accessor,
    label,
    additionalLabel,
    format = "number",
    showScope = true,
    symbol = null,
  ) => {
    const formattedLabel = additionalLabel ? `${label} ${additionalLabel}` : label[0].toUpperCase() + label.slice(1);

    // List out available operators => default to number

    // Returns list of operators based on accessor
    return {
      group: group,
      supportedOperators: OPERATOR_OPTIONS[format],
      value: `\${${accessor}}`,
      label: formattedLabel,
      scopeText: scopeText(scope),
      symbol,
      showScope,
    };
  };

  const optionsByEngine = {
    pricing: [
      {
        value: `\${units}`,
        label: "Units",
        group: "units",
        scopeText: scopeText(scope),
        supportedOperators: OPERATOR_OPTIONS["number"],
      },
      {
        group: "bsr",
        value: `\${bsr}`,
        label: "Average BSR",
        scopeText: scopeText(scope),
        supportedOperators: OPERATOR_OPTIONS["number"],
      },
    ],
    bidding: [
      makeTargetConditionOption("target_match_types", "targets"),
      makeTargetConditionOption("keyword_match_types", "keywords"),
      makeBiddingConditionOption("ad_sales", "ad_sales", "ad sales", null, "number", true, "currency"),
      makeBiddingConditionOption(
        "ad_sales_trend",
        "ad_sales_trend",
        "Ad sales trend %",
        null,
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("conversions", "conversions", "conversions"),
      makeBiddingConditionOption(
        "conversions_trend",
        "conversions_trend",
        "Conversion trend %",
        null,
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("acos", "acos", "ACOS", null, "number", true, "percent"),
      makeBiddingConditionOption("roas", "roas", "ROAS"),
      makeBiddingConditionOption("ad_spend", "ad_spend", "ad spend", null, "number", true, "currency"),
      makeBiddingConditionOption("conv_rate", "conv_rate", "conversion rate", null, "number", true, "percent"),
      makeBiddingConditionOption("ctr", "click_thru_rate", "click-through rate", null, "number", true, "percent"),
      makeBiddingConditionOption(
        "revenue_per_click",
        "revenue_per_click",
        "Revenue per Click",
        null,
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption("clicks", "clicks", "clicks"),
      makeBiddingConditionOption("clicks_trend", "clicks_trend", "Clicks trend %", null, "number", true, "percent"),
      makeBiddingConditionOption("bid", "bid", "Current Bid", null, "number", false, "currency"),
      makeBiddingConditionOption("impressions", "impressions", "impressions"),
      makeBiddingConditionOption(
        "impressions_trend",
        "impressions_trend",
        "Impression trend %",
        null,
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("cpc", "cpc", "CPC", null, "number", true, "currency"),
      makeBiddingConditionOption("tacos", "tacos", "TACOS", null, "number", true, "percent"),
      makeBiddingConditionOption("troas", "troas", "TROAS"),
      makeBiddingConditionOption("total_cr", "total_cr", "total conversion rate", null, "number", true, "percent"),
      makeBiddingConditionOption("ad.serving_hours", "ad.serving_hours", "Campaign Serving Hours"),
      makeBiddingConditionOption(
        "ad.budget_percent",
        "ad.budget_percent",
        "Campaign Spend % of Budget",
        null,
        "number",
        true,
        "percent",
      ),
    ],
    budget: [
      makeBiddingConditionOption("ad_sales", "ad_sales", "ad sales", null, "number", true, "currency"),
      makeBiddingConditionOption(
        "ad_sales_trend",
        "ad_sales_trend",
        "Ad sales trend %",
        null,
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("conversions", "conversions", "conversions"),
      makeBiddingConditionOption(
        "conversions_trend",
        "conversions_trend",
        "Conversion trend %",
        null,
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("acos", "acos", "ACOS", null, "number", true, "percent"),
      makeBiddingConditionOption("roas", "roas", "ROAS"),
      makeBiddingConditionOption("impressions", "impressions", "impressions"),
      makeBiddingConditionOption(
        "impressions_trend",
        "impressions_trend",
        "Impression trend %",
        null,
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("ad_spend", "ad_spend", "ad spend", null, "number", true, "currency"),
      makeBiddingConditionOption("conv_rate", "conv_rate", "conversion rate", null, "number", true, "percent"),
      makeBiddingConditionOption("ctr", "click_thru_rate", "click-through rate", null, "number", true, "percent"),
      makeBiddingConditionOption(
        "revenue_per_click",
        "revenue_per_click",
        "Revenue per Click",
        null,
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption("clicks", "clicks", "clicks"),
      makeBiddingConditionOption("clicks_trend", "clicks_trend", "Clicks trend %", null, "number", true, "percent"),
      makeBiddingConditionOption("serving_hours", "serving_hours", "Serving Hours"),
      makeBiddingConditionOption(
        "budget_percent",
        "budget_percent",
        "Spend % of Budget",
        null,
        "number",
        true,
        "percent",
      ),
      ...[
        ["PLACEMENT_TOP", "Top of Search"],
        ["PLACEMENT_PRODUCT_PAGE", "Detail Page"],
        ["PLACEMENT_REST_OF_SEARCH", "Rest of Search"],
      ].reduce((conditions, [placement, prefix]) => {
        conditions.push(
          makeBiddingConditionOption(
            `${placement}`,
            `${placement}`,
            `${prefix}: Current Multiplier`,
            null,
            "number",
            false,
            "percent",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.acos`,
            `${placement}.acos`,
            `${prefix}: ACOS`,
            null,
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(makeBiddingConditionOption(`${placement}.roas`, `${placement}.roas`, `${prefix}: ROAS`));
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.ad_spend`,
            `${placement}.ad_spend`,
            `${prefix}: ad spend`,
            null,
            "number",
            true,
            "currency",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.conv_rate`,
            `${placement}.conv_rate`,
            `${prefix}: conversion rate`,
            null,
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(makeBiddingConditionOption(`${placement}.clicks`, `${placement}.clicks`, `${prefix}: clicks`));
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.click_share`,
            `${placement}.click_share`,
            `${prefix}: click share`,
            null,
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(`${placement}.impressions`, `${placement}.impressions`, `${prefix}: impressions`),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.impression_share`,
            `${placement}.impression_share`,
            `${prefix}: impression share`,
            null,
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.ctr`,
            `${placement}.click_thru_rate`,
            `${prefix}: click-through rate`,
            null,
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(`${placement}.conversions`, `${placement}.conversions`, `${prefix}: conversions`),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.revenue_per_click`,
            `${placement}.revenue_per_click`,
            `${prefix}: Revenue per Click`,
            null,
            "number",
            true,
            "currency",
          ),
        );
        return conditions;
      }, []),
    ],
    lineitem_bidding: [
      makeBiddingConditionOption("tacos", "tacos", "total ACOS"),
      makeBiddingConditionOption("troas", "troas", "total ROAS"),
      makeBiddingConditionOption("ctr", "click_thru_rate", "click-through rate"),
      makeBiddingConditionOption("impressions", "impressions", "impressions"),
      makeBiddingConditionOption("clicks", "clicks", "clicks"),
      makeBiddingConditionOption("cost", "cost", "cost"),
      makeBiddingConditionOption("ecpm", "ecpm", "eCPM"),
      makeBiddingConditionOption("total_detailed_page_view_rate", "total_detailed_page_view_rate", "total DPV rate"),
      makeBiddingConditionOption("total_add_to_cart_rate", "total_add_to_cart_rate", "total ATC rate"),
      makeBiddingConditionOption(
        "new_to_brand_detail_page_view_rate",
        "new_to_brand_detail_page_view_rate",
        "NTB DPV rate",
      ),
      makeBiddingConditionOption("total_new_to_brand_dpv_rate", "total_new_to_brand_dpv_rate", "total NTB DPV rate"),
      makeBiddingConditionOption("brand_search_rate", "brand_search_rate", "Brand Search Rate"),
      makeBiddingConditionOption("total_units_sold", "total_units_sold", "Total Units Sold"),
      makeBiddingConditionOption("viewable_impressions", "viewable_impressions", "Viewable Impressions"),
      makeBiddingConditionOption("viewability", "viewability", "Viewability"),
    ],
    harvesting_query_match: [makeSearchQueryConditionOption("query", "Search Query")],
    harvesting: [
      makeBiddingConditionOption("conversions", "conversions", "conversions"),
      makeBiddingConditionOption("units_sold", "units_sold", "units sold"),
      makeBiddingConditionOption("clicks", "clicks", "clicks"),
      makeBiddingConditionOption("impressions", "impressions", "impressions"),
      makeBiddingConditionOption("ad_spend", "ad_spend", "ad spend", null, "number", true, "currency"),
      makeBiddingConditionOption("acos", "acos", "ACOS", null, "number", true, "percent"),
      makeBiddingConditionOption("roas", "roas", "ROAS"),
      makeBiddingConditionOption("conv_rate", "conv_rate", "conversion rate", null, "number", true, "percent"),
      makeBiddingConditionOption("ctr", "click_thru_rate", "click-through rate", null, "number", true, "percent"),
    ],
  };

  return optionsByEngine[engine];
};

export default makeConditionOptions;
